/* eslint-disable no-param-reassign */

import Cookies from 'universal-cookie';
import { object, string } from 'yup';
import { z } from 'zod';

import type { Register } from '@/pages/dashboard/auth/signup';

import { axios } from '../axios';
import { apiRoutes } from '../config';

axios.defaults.withCredentials = true;

axios.interceptors.request.use(
  (config) => {
    const cookies = new Cookies();
    config.headers['X-CSRFToken'] = cookies.get('csrftoken');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err?.response?.config;
    if (
      err?.response?.status === 401 &&
      !originalConfig._retry &&
      originalConfig?.url !== apiRoutes.auth.token.refresh
    ) {
      originalConfig._retry = true;
      try {
        await axios.post(apiRoutes.auth.token.refresh);
        return await axios(originalConfig);
      } catch (_error) {
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export enum EnumAccountType {
  ADMIN = 'ADMIN',
  CANDIDATE = 'CANDIDATE',
  CUSTOMER = 'CUSTOMER',
  RECRUITER = 'RECRUITER',
  DEMO_COMPETENCY_ASSESSMENT = 'DEMO_COMPETENCY_ASSESSMENT',
}

const googleOneTapSchema = z.object({
  user: z.object({
    email: z.string(),
    firstName: z.string(),
    id: z.number(),
    lastName: z.string(),
    accountType: z.nativeEnum(EnumAccountType),
  }),
});

export const login = (data: {
  email: string;
  password: string;
  rememberMe?: boolean | undefined;
}) => axios.post(apiRoutes.auth.token._root, data);

export const register = ({
  callbackUrl,
  ...data
}: Register & { callbackUrl?: string | null }) =>
  axios.post(apiRoutes.auth.customerRegister, {
    ...data,
    callback_url:
      callbackUrl === null
        ? undefined
        : callbackUrl ?? window.location.pathname,
  });

export const candidateRegister = (data: {
  name: string;
  email: string;
  password: string;
  callbackUrl?: string;
  smsAccountConsent?: boolean;
  smsMarketingConsent?: boolean;
}) => axios.post(apiRoutes.auth.register, data);

export const requestAccountActivate = (email: string) =>
  axios.post(apiRoutes.auth.activateRequest, { email });

export const resetPasswordRequest = (email: string) =>
  axios.post(apiRoutes.auth.resetPasswordRequest, {
    email,
  });

export const activateAccount = (token: string) =>
  axios.post(apiRoutes.auth.activate, {
    token,
  });

export const postActiveCandidateStatusRequest = (data: {
  token: string;
  userChoice: boolean;
}) => axios.post(apiRoutes.auth.activeCandidateStatusRequest, data);

export const getGoogleAuth = async ({
  accountType,
  callbackUrl,
}: {
  accountType: EnumAccountType;
  callbackUrl?: string | null;
}) => {
  const { data } = await axios.get(apiRoutes.auth.google._root, {
    params: {
      accountType,
      callback_url:
        callbackUrl === null
          ? undefined
          : callbackUrl ?? window.location.pathname,
    },
    withCredentials: true,
  });
  const validData = object({
    authorizationUrl: string().url().defined(),
  }).validate(data);
  return validData;
};

export const getLinkedInAuth = async ({
  accountType,
  callbackUrl,
}: {
  accountType: EnumAccountType;
  callbackUrl?: string | null;
}) => {
  const { data } = await axios.get(
    accountType === EnumAccountType.CANDIDATE
      ? apiRoutes.auth.linkedin.loginCandidate
      : apiRoutes.auth.linkedin.loginCustomer,
    {
      params: {
        accountType,
        callback_url:
          callbackUrl === null
            ? undefined
            : callbackUrl ?? window.location.pathname,
      },
      withCredentials: true,
    }
  );
  const validData = object({
    authorizationUrl: string().url().defined(),
  }).validate(data);
  return validData;
};

export const postGoogleOneTap = async ({
  credential,
  accountType,
  callbackUrl,
}: {
  credential: string;
  accountType: EnumAccountType;
  callbackUrl?: string | null;
}) => {
  const { data } = await axios.post(apiRoutes.auth.google.oneTap, {
    token: credential,
    accountType,
    callback_url:
      callbackUrl === null
        ? undefined
        : callbackUrl ?? window.location.pathname,
  });
  return googleOneTapSchema.parse(data);
};

export const resetPassword = (formData: {
  token: string;
  password: string;
  passwordRepeated: string;
}) => axios.post(apiRoutes.auth.resetPassword, formData);

export default axios;
